import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Download, Upload, Flag, X, Search, BadgeCheck, Trash2, RotateCcw, Wrench, Package, Radio, Scale, Building2, Palette, Monitor, Volume2 } from "lucide-react";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import Seo from "@/components/Seo";
import api, { formatApiErrorDetail } from "@/lib/api";
import { installPack, isPackInstalled } from "@/components/cef/packs";

const CATEGORIES = ["Models", "Maps", "Scripts", "Vehicles", "Audio", "Gameplay", "Config Pack", "Other"];
const PLAYER_SIDE = new Set(["menu_theme", "boot_pack", "sound_pack", "cad_theme", "phone_theme"]);
const PACK_ICONS = { menu_theme: Palette, boot_pack: Monitor, sound_pack: Volume2, cad_theme: Package, phone_theme: Package, penal_code: Scale, radio_plan: Radio, dept_structure: Building2, court_system: Scale, government: Building2, loading_screen: Package };
const PACK_COLORS = { menu_theme: "text-nc-blue", boot_pack: "text-emerald-400", sound_pack: "text-fuchsia-400", cad_theme: "text-amber-400", phone_theme: "text-rose-400", penal_code: "text-nc-blue", radio_plan: "text-nc-green", dept_structure: "text-nc-orange", court_system: "text-red-400", government: "text-yellow-400", loading_screen: "text-zinc-300" };
const inputCls = "w-full bg-[#050505] border border-white/10 px-3 py-2.5 font-mono text-xs text-zinc-100 rounded-none focus:outline-none focus:ring-1 focus:ring-nc-blue focus:border-nc-blue";
const labelCls = "font-mono text-[9px] tracking-[0.25em] text-zinc-500 uppercase block mb-1.5";

const EMPTY_FORM = { title: "", description: "", category: "Models", version: "1.0", file_url: "", image_url: "", tags: "" };

export default function WorkshopPage() {
  const [me, setMe] = useState(null);
  const [mods, setMods] = useState([]);
  const [category, setCategory] = useState(() => new URLSearchParams(window.location.search).get("category") || "");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("new");
  const [showUpload, setShowUpload] = useState(false);
  const [reportFor, setReportFor] = useState(null);
  const [reportReason, setReportReason] = useState("");
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = useCallback(() => {
    const params = {};
    if (category) params.category = category;
    if (q.trim()) params.q = q.trim();
    if (sort) params.sort = sort;
    api.get("/workshop/mods", { params }).then((r) => setMods(r.data)).catch(() => {});
    api.get("/auth/me").then((r) => setMe(r.data)).catch(() => setMe(null));
  }, [category, q, sort]);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/workshop/mods", { ...form, tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean) });
      toast.success("Mod published to the workshop");
      setForm(EMPTY_FORM); setShowUpload(false); load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const [installing, setInstalling] = useState({}); // mod_id -> owned servers list while picking
  const [packInstalled, setPackInstalled] = useState(() => new Set(Object.values(JSON.parse(localStorage.getItem("nc_packs_v2") || "{}")).flat().map((p) => p.id)));

  // native config packs: player-side packs (themes/boots/sounds) install to the client;
  // server packs apply to a verified server you own (owner install = the approval step).
  const install = async (mod) => {
    if (PLAYER_SIDE.has(mod.pack_type)) {
      installPack(mod);
      setPackInstalled((s) => new Set([...s, mod.id]));
      setMods((ms) => ms.map((x) => (x.id === mod.id ? { ...x, downloads: x.downloads + 1 } : x)));
      api.post(`/workshop/mods/${mod.id}/download`).catch(() => {});
      const where = mod.pack_type === "menu_theme" ? "Main Menu → Settings → Menu Theme"
        : mod.pack_type === "boot_pack" ? "Settings → Menu Theme → Boot Sequence"
        : mod.pack_type === "sound_pack" ? "Settings → Menu Theme → Sound Pack"
        : "applies right now";
      toast.success(`${mod.title} installed — ${where}`);
      return;
    }
    if (!me) { toast.error("Sign in to install server packs"); return; }
    try {
      const r = await api.get("/nc/servers");
      const mine = (r.data.servers || []).filter((s) => s.verified && (s.owner_id === me.id || me.role === "admin"));
      if (!mine.length) { toast.error("You need a verified server — register & claim one in the server browser first"); return; }
      if (mine.length === 1) return applyPack(mod, mine[0].id);
      setInstalling((p) => ({ ...p, [mod.id]: mine }));
    } catch { toast.error("Could not load your servers"); }
  };
  const applyPack = async (mod, serverId) => {
    try {
      const r = await api.post(`/workshop/mods/${mod.id}/install`, { server_id: serverId });
      toast.success(r.data.message);
      setMods((ms) => ms.map((x) => (x.id === mod.id ? { ...x, downloads: x.downloads + 1 } : x)));
      setInstalling((p) => ({ ...p, [mod.id]: null }));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const disablePack = async (mod, serverId) => {
    try {
      const r = await api.post(`/workshop/mods/${mod.id}/uninstall`, { server_id: serverId });
      toast.success(r.data.message);
      setInstalling((p) => ({ ...p, [mod.id]: null }));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const download = async (mod) => {
    try {
      const r = await api.post(`/workshop/mods/${mod.id}/download`);
      window.open(r.data.file_url, "_blank", "noopener");
      setMods((ms) => ms.map((m) => (m.id === mod.id ? { ...m, downloads: m.downloads + 1 } : m)));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const report = async () => {
    setBusy(true);
    try {
      await api.post(`/workshop/mods/${reportFor.id}/report`, { reason: reportReason });
      toast.success("Report received — the studio reviews flagged mods");
      setReportFor(null); setReportReason(""); load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const removeMod = async (mod) => {
    try {
      await api.delete(`/workshop/mods/${mod.id}`);
      toast.success("Mod removed");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const restoreMod = async (mod) => {
    try {
      await api.post(`/workshop/mods/${mod.id}/restore`);
      toast.success("Mod restored");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="workshop-page" className="min-h-screen bg-[#050505] text-zinc-100 flex flex-col selection:bg-nc-blue/30">
      <Seo
        title="New Citizen Mod Workshop — Download & Upload Mods"
        description="The official New Citizen Mod Workshop. Browse community mods — models, maps, scripts, vehicles, audio and gameplay — download free, upload your own, and report violations."
        path="/new-citizen/workshop"
        keywords="new citizen mods, new citizen workshop, new citizen modding, download new citizen mods, new citizen mod upload"
      />
      <SiteNav />

      <header className="border-b border-white/10 relative overflow-hidden">
        <div className="absolute inset-0 grid-texture opacity-50" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-14">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <p className="font-mono text-xs tracking-[0.4em] text-nc-blue uppercase mb-4">New Citizen // Official Mod Workshop</p>
            <h1 data-testid="workshop-title" className="font-sans font-bold uppercase tracking-tight text-3xl sm:text-5xl text-zinc-50">
              The Mod <span className="text-nc-blue">Workshop</span>
            </h1>
            <p className="font-mono text-sm text-zinc-400 mt-5 max-w-2xl leading-relaxed">
              The official community workshop for New Citizen. Browse verified mods, download free,
              publish your own creations — and keep the city clean by reporting anything that violates the rules.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <button
                data-testid="workshop-upload-cta"
                onClick={() => (me ? setShowUpload(true) : toast.error("Sign in to publish mods"))}
                className="font-mono text-xs tracking-[0.25em] uppercase bg-nc-blue text-white px-6 py-3.5 hover:bg-nc-blue/80 transition-colors duration-300 inline-flex items-center gap-2"
              >
                <Upload className="w-4 h-4" /> Publish a Mod
              </button>
              <Link to="/new-citizen" className="font-mono text-xs tracking-[0.25em] uppercase border border-white/20 text-zinc-200 px-6 py-3.5 hover:border-nc-blue hover:text-nc-blue transition-colors duration-300">
                About New Citizen
              </Link>
            </div>
            <div data-testid="workshop-notice" className="mt-7 max-w-2xl border border-nc-green/40 bg-nc-green/5 px-5 py-4">
              <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-nc-green mb-1.5">Config packs are live</p>
              <p className="font-mono text-xs text-zinc-300 leading-relaxed">
                Official Specpixel packs install in one click — menu themes reskin your main menu player-side, and server packs (penal codes, radio plans, department structures) apply straight to a verified server you own. Creator publishing is open too.
              </p>
            </div>
          </motion.div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-10 w-full flex flex-col gap-6">
        <div data-testid="workshop-body">
        {/* Controls */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px] max-w-sm">
            <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <input data-testid="workshop-search" className={`${inputCls} pl-9`} placeholder="Search mods, tags, authors…" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <select data-testid="workshop-sort" className={`${inputCls} w-auto`} value={sort} onChange={(e) => setSort(e.target.value)}>
            <option value="new">Newest first</option>
            <option value="downloads">Most downloaded</option>
          </select>
          <div className="flex flex-wrap gap-2">
            <button data-testid="workshop-cat-all" onClick={() => setCategory("")} className={`font-mono text-[9px] tracking-[0.2em] uppercase border px-3 py-1.5 transition-colors ${category === "" ? "text-nc-blue border-nc-blue/60 bg-nc-blue/10" : "text-zinc-400 border-white/10 hover:border-white/30"}`}>All</button>
            {CATEGORIES.map((c) => (
              <button key={c} data-testid={`workshop-cat-${c.toLowerCase()}`} onClick={() => setCategory(category === c ? "" : c)} className={`font-mono text-[9px] tracking-[0.2em] uppercase border px-3 py-1.5 transition-colors ${category === c ? "text-nc-blue border-nc-blue/60 bg-nc-blue/10" : "text-zinc-400 border-white/10 hover:border-white/30"}`}>{c}</button>
            ))}
          </div>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {mods.length === 0 && (
            <div data-testid="workshop-empty" className="col-span-full border border-dashed border-white/15 p-14 text-center">
              <Wrench className="w-8 h-8 text-zinc-700 mx-auto mb-4" strokeWidth={1.5} />
              <p className="font-mono text-xs text-zinc-500">The workshop is quiet — be the first to publish a New Citizen mod.</p>
            </div>
          )}
          {mods.map((m, i) => (
            <motion.div key={m.id} data-testid={`mod-card-${i + 1}`} className={`border bg-black/40 flex flex-col ${m.status === "flagged" ? "border-nc-orange/50" : "border-white/10"} hover:border-nc-blue/50 transition-colors duration-300`} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.35, delay: (i % 3) * 0.05 }}>
              {m.image_url ? (
                <div className="h-40 border-b border-white/10 overflow-hidden bg-black/60">
                  <img src={m.image_url} alt={m.title} className="w-full h-full object-cover" loading="lazy" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                </div>
              ) : m.pack_type === "menu_theme" && m.pack?.theme ? (
                <div className="h-40 border-b border-white/10 relative overflow-hidden flex flex-col items-center justify-center gap-2" style={{ background: `linear-gradient(160deg, ${m.pack.theme.tint || "#0a0a0a"}, #000)` }}>
                  <div className="absolute top-0 inset-x-0 flex h-2">{(m.pack.theme.stripes || []).map((c, i) => <span key={i} className="flex-1" style={{ backgroundColor: c }} />)}</div>
                  <p className="font-bold text-lg tracking-[0.15em] uppercase" style={{ color: m.pack.theme.accent }}>{m.pack.theme.boot_title || "MENU THEME"}</p>
                  <p className="font-mono text-[8px] uppercase tracking-[0.3em] text-zinc-500">menu theme pack</p>
                </div>
              ) : m.pack_type ? (
                <div className="h-40 border-b border-white/10 bg-black/60 grid-texture flex flex-col items-center justify-center gap-2">
                  {(() => { const Icon = PACK_ICONS[m.pack_type] || Package; return <Icon className={`w-8 h-8 ${PACK_COLORS[m.pack_type] || "text-nc-blue"}`} strokeWidth={1.5} />; })()}
                  <p className="font-mono text-[8px] uppercase tracking-[0.3em] text-zinc-500">{m.pack_type.replace(/_/g, " ")} pack</p>
                  {["court_system", "government", "penal_code"].includes(m.pack_type) && (
                    <p className="font-mono text-[7px] uppercase tracking-[0.2em] text-nc-orange border border-nc-orange/40 px-1.5 py-0.5">gameplay · owner-approved</p>
                  )}
                </div>
              ) : (
                <div className="h-40 border-b border-white/10 bg-black/60 grid-texture flex items-center justify-center">
                  <Wrench className="w-8 h-8 text-zinc-700" strokeWidth={1.5} />
                </div>
              )}
              <div className="border-b border-white/10 px-4 py-2 flex items-center justify-between gap-2">
                <span className="font-mono text-[8px] tracking-[0.2em] uppercase text-nc-blue border border-nc-blue/40 bg-nc-blue/10 px-2 py-0.5">{m.category}</span>
                <div className="flex items-center gap-2">
                  {m.official && <span data-testid={`mod-official-${i + 1}`} className="font-mono text-[8px] tracking-[0.2em] uppercase text-nc-green border border-nc-green/40 bg-nc-green/10 px-1.5 py-0.5">Official</span>}
                  <span className="font-mono text-[8px] text-zinc-600">v{m.version}</span>
                  {m.status === "flagged" && <span data-testid={`mod-flagged-${i + 1}`} className="font-mono text-[8px] tracking-[0.2em] uppercase text-nc-orange border border-nc-orange/40 bg-nc-orange/10 px-1.5 py-0.5">Flagged</span>}
                </div>
              </div>
              <div className="p-4 flex flex-col gap-2 flex-1">
                <p className="font-sans font-bold uppercase text-sm text-zinc-100 leading-tight">{m.title}</p>
                <p className="font-mono text-[10px] text-zinc-500 line-clamp-3 leading-relaxed">{m.description}</p>
                <p className="font-mono text-[9px] text-zinc-600 flex items-center gap-1.5 mt-auto pt-1">
                  {m.author_verified && <BadgeCheck className="w-3.5 h-3.5 text-nc-blue" />} {m.author_name} // {String(m.created_at).slice(0, 10)}
                </p>
              </div>
              <div className="border-t border-white/10 px-4 py-2.5 flex flex-col gap-2">
                <div className="flex items-center gap-2">
                {m.pack_type ? (
                  <button data-testid={`mod-install-${i + 1}`} onClick={() => install(m)} disabled={PLAYER_SIDE.has(m.pack_type) && packInstalled.has(m.id)}
                    className="flex-1 font-mono text-[9px] tracking-[0.2em] uppercase bg-nc-green hover:bg-nc-green/80 disabled:opacity-60 text-black px-3 py-2 transition-colors flex items-center justify-center gap-1.5">
                    <Package className="w-3.5 h-3.5" />
                    {PLAYER_SIDE.has(m.pack_type) ? (packInstalled.has(m.id) ? "Installed" : "Install") : `Install to server (${m.downloads})`}
                  </button>
                ) : (
                  <button data-testid={`mod-download-${i + 1}`} onClick={() => download(m)} className="flex-1 font-mono text-[9px] tracking-[0.2em] uppercase bg-nc-blue hover:bg-nc-blue/80 text-white px-3 py-2 transition-colors flex items-center justify-center gap-1.5">
                    <Download className="w-3.5 h-3.5" /> Download ({m.downloads})
                  </button>
                )}
                <button data-testid={`mod-report-${i + 1}`} onClick={() => (me ? setReportFor(m) : toast.error("Sign in to report mods"))} title="Report this mod" className="font-mono text-[9px] uppercase border border-white/15 text-zinc-400 hover:border-nc-orange hover:text-nc-orange px-2.5 py-2 transition-colors">
                  <Flag className="w-3.5 h-3.5" />
                </button>
                {(me?.role === "admin" || me?.id === m.author_id) && (
                  <button data-testid={`mod-remove-${i + 1}`} onClick={() => removeMod(m)} title="Remove" className="font-mono text-[9px] uppercase border border-red-400/40 text-red-400 hover:bg-red-400/10 px-2.5 py-2 transition-colors">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
                {me?.role === "admin" && m.status === "flagged" && (
                  <button data-testid={`mod-restore-${i + 1}`} onClick={() => restoreMod(m)} title="Restore (clear reports)" className="font-mono text-[9px] uppercase border border-nc-green/40 text-nc-green hover:bg-nc-green/10 px-2.5 py-2 transition-colors">
                    <RotateCcw className="w-3.5 h-3.5" />
                  </button>
                )}
                </div>
                {installing[m.id] && (
                  <div data-testid={`mod-server-pick-${i + 1}`} className="flex flex-col gap-1">
                    <span className="font-mono text-[8px] uppercase text-zinc-500">Pick a verified server:</span>
                    {installing[m.id].map((s) => {
                      const has = (s.active_mods || []).some((am) => am.id === m.id);
                      return (
                        <button key={s.id} data-testid={`mod-server-${s.id.slice(0, 8)}`}
                          onClick={() => has ? disablePack(m, s.id) : applyPack(m, s.id)}
                          className={`text-left font-mono text-[9px] border px-2.5 py-1.5 transition-colors flex items-center justify-between gap-2 ${has ? "border-nc-orange/40 text-nc-orange hover:bg-nc-orange/10" : "border-white/15 text-zinc-200 hover:border-nc-green hover:text-nc-green"}`}>
                          <span className="truncate">{s.name} ({s.id})</span>
                          <span className="uppercase text-[8px] shrink-0">{has ? "Running — click to disable" : "Install"}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </div>
        </div>
      </main>

      {/* Upload modal */}
      <AnimatePresence>
        {showUpload && (
          <div data-testid="workshop-upload-modal" className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowUpload(false)}>
            <form onSubmit={submit} className="max-w-xl w-full border border-white/15 bg-[#0a0a0a] max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="border-b border-white/10 px-5 py-4 flex items-center justify-between sticky top-0 bg-[#0a0a0a]">
                <p className="font-sans font-bold uppercase text-base text-zinc-50">Publish a Mod</p>
                <button type="button" onClick={() => setShowUpload(false)} className="text-zinc-500 hover:text-white"><X className="w-5 h-5" /></button>
              </div>
              <div className="p-5 flex flex-col gap-4">
                <p className="font-mono text-[9px] text-zinc-600 leading-relaxed uppercase tracking-[0.15em]">External download links only (Nexus, GitHub, Mediafire, Google Drive). Original work only — stolen content is removed and accounts suspended.</p>
                <div>
                  <label className={labelCls}>Mod title *</label>
                  <input data-testid="upload-title" required minLength={4} className={inputCls} value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. Downtown HD Overhaul" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>Category *</label>
                    <select data-testid="upload-category" className={inputCls} value={form.category} onChange={(e) => set("category", e.target.value)}>
                      {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Version</label>
                    <input data-testid="upload-version" className={inputCls} value={form.version} onChange={(e) => set("version", e.target.value)} placeholder="1.0" />
                  </div>
                </div>
                <div>
                  <label className={labelCls}>Download link *</label>
                  <input data-testid="upload-file-url" required className={inputCls} value={form.file_url} onChange={(e) => set("file_url", e.target.value)} placeholder="https://nexusmods.com/… or https://github.com/…" />
                </div>
                <div>
                  <label className={labelCls}>Preview image link (optional)</label>
                  <input data-testid="upload-image-url" className={inputCls} value={form.image_url} onChange={(e) => set("image_url", e.target.value)} placeholder="https://i.imgur.com/…" />
                </div>
                <div>
                  <label className={labelCls}>Tags (comma separated)</label>
                  <input data-testid="upload-tags" className={inputCls} value={form.tags} onChange={(e) => set("tags", e.target.value)} placeholder="textures, hd, downtown" />
                </div>
                <div>
                  <label className={labelCls}>Description *</label>
                  <textarea data-testid="upload-description" required minLength={20} rows={4} className={`${inputCls} resize-none`} value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="What the mod does, requirements, install instructions…" />
                </div>
                <button data-testid="upload-submit" disabled={busy} className="bg-nc-blue hover:bg-nc-blue/80 disabled:opacity-40 text-white font-sans font-semibold uppercase tracking-[0.2em] text-xs px-6 py-3 flex items-center justify-center gap-2 transition-colors">
                  <Upload className="w-4 h-4" /> {busy ? "Publishing…" : "Publish to Workshop"}
                </button>
              </div>
            </form>
          </div>
        )}
      </AnimatePresence>

      {/* Report modal */}
      <AnimatePresence>
        {reportFor && (
          <div data-testid="workshop-report-modal" className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setReportFor(null)}>
            <div className="max-w-md w-full border border-white/15 bg-[#0a0a0a]" onClick={(e) => e.stopPropagation()}>
              <div className="border-b border-white/10 px-5 py-4 flex items-center justify-between">
                <p className="font-sans font-bold uppercase text-base text-zinc-50">Report — {reportFor.title}</p>
                <button onClick={() => setReportFor(null)} className="text-zinc-500 hover:text-white"><X className="w-5 h-5" /></button>
              </div>
              <div className="p-5 flex flex-col gap-4">
                <p className="font-mono text-[10px] text-zinc-500 leading-relaxed">Report stolen content, malware, rule violations or mislabeled files. Mods with {3}+ reports are flagged for studio review.</p>
                <textarea data-testid="report-reason" rows={3} className={`${inputCls} resize-none`} value={reportReason} onChange={(e) => setReportReason(e.target.value)} placeholder="Why are you reporting this mod? *" />
                <button data-testid="report-submit" disabled={busy || reportReason.trim().length < 5} onClick={report} className="bg-nc-orange hover:bg-nc-orange/80 disabled:opacity-40 text-black font-sans font-bold uppercase tracking-[0.2em] text-xs px-6 py-3 transition-colors">
                  Submit Report
                </button>
              </div>
            </div>
          </div>
        )}
      </AnimatePresence>

      <SiteFooter />
    </div>
  );
}
